import { useEffect, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import AssessmentFormPage from "../AssessmentFormPage";
import CompanyProfilePage from "../CompanyProfilePage";
import FirstRunChecklistCard from "../FirstRunChecklistCard";
import ReportProfilesPage from "../ReportProfilesPage";
import WorkspaceKnowledgePage from "../WorkspaceKnowledgePage";
import NexusSurfaceShell from "./NexusSurfaceShell";
import { LensCompaniesIcon, LensKnowledgeIcon, LensReportsIcon, LensSurveyIcon } from "./surfaceIcons";

const LENSES = [
  { id: "companies", label: "Companies", desc: "Profiles · assessment assignment", icon: <LensCompaniesIcon /> },
  { id: "survey", label: "Survey", desc: "Assessments · questions", icon: <LensSurveyIcon /> },
  { id: "reports", label: "Reports", desc: "PDF profile builder", icon: <LensReportsIcon /> },
  {
    id: "knowledge",
    label: "Knowledge",
    desc: "Brief · themes for NEXUS",
    icon: <LensKnowledgeIcon />,
  },
];

export default function StudioPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const lensParam = searchParams.get("lens");
  const templateId = searchParams.get("template_id") || "";
  const organizationId = searchParams.get("organization_id") || "";
  const fromFiveX = searchParams.get("from") === "5x";
  // Legacy ?lens=formats redirects into Survey (formats live inside New assessment).
  const lens =
    lensParam === "knowledge"
      ? "knowledge"
      : lensParam === "reports"
        ? "reports"
        : lensParam === "survey" || lensParam === "formats"
          ? "survey"
          : "companies";
  const { setAdminAssistantContext } = useOutletContext() || {};
  const [checklistRefresh, setChecklistRefresh] = useState(0);

  const setLens = (id) => {
    const next = id === "companies" ? {} : { lens: id };
    setSearchParams(next);
  };

  useEffect(() => {
    if (lensParam === "formats") {
      setSearchParams({ lens: "survey" }, { replace: true });
    }
  }, [lensParam, setSearchParams]);

  useEffect(() => {
    // Report Profiles page sets a richer assistant context when mounted.
    if (lens === "reports") return undefined;
    const pageMap = {
      companies: "company_profile",
      survey: "assessment_form",
      reports: "report_profiles",
      knowledge: "studio_knowledge",
    };
    setAdminAssistantContext?.({
      page: pageMap[lens] || "studio",
      surface: "studio",
      currentRoute: `/studio${lens === "companies" ? "" : `?lens=${lens}`}`,
      scope: {
        label: "Workspace Studio",
        text: LENSES.find((l) => l.id === lens)?.desc || "Configuration hub",
      },
      activeLens: lens,
    });
    return () => setAdminAssistantContext?.(null);
  }, [lens, setAdminAssistantContext]);

  const titles = {
    companies: "Company workspaces",
    survey: "Assessments",
    reports: "Report profiles",
    knowledge: "Workspace knowledge",
  };

  const subtitles = {
    companies: "Identity, financials, survey pause, and which assessment a company uses.",
    survey: "Create an assessment (sections + questions), edit wording, and activate for invites.",
    reports: "Build company and team PDF templates from modular content blocks.",
  knowledge: "",
};

  return (
    <NexusSurfaceShell
      surfaceId="studio"
      eyebrow="Workspace Studio"
      title={titles[lens]}
      subtitle={subtitles[lens] || null}
      lenses={LENSES}
      activeLens={lens}
      onLensChange={setLens}
    >
      {lens === "companies" ? (
        <>
          <FirstRunChecklistCard refreshKey={checklistRefresh} />
          <CompanyProfilePage
            embedded
            onCompanySaved={() => setChecklistRefresh((n) => n + 1)}
          />
        </>
      ) : lens === "survey" ? (
        <AssessmentFormPage
          embedded
          templateId={templateId}
          organizationId={organizationId}
          preferFiveXTemplate={fromFiveX}
          onAssessmentCreated={() => setChecklistRefresh((n) => n + 1)}
        />
      ) : lens === "reports" ? (
        <ReportProfilesPage embedded />
      ) : (
        <WorkspaceKnowledgePage embedded />
      )}
    </NexusSurfaceShell>
  );
}
